import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';
import { IRegionProps } from '@source/interface';
import Layout from '@source/components/Layout';

import CCPOPageBanner from '@source/pages/CarroCertified/components/CCPOBanner';
import CCPOPageHighlights from '@source/pages/CarroCertified/components/CCPOHighlights';
import CCPOPageInspection160Point from '@source/pages/CarroCertified/components/CCPOPageInspection160Point';
import CCPOPageFeatures from '@source/pages/CarroCertified/components/CCPOPageFeatures';
import CCPOBrowseCards from '@source/pages/CarroCertified/components/CCPOBrowseCards';
import { CustomerReview } from '@source/components/CustomerReview';
import { getReviewCCPO } from '@source/pages/CarroCertified/constants';
import StyledContainer from '@design-system/styled/Container';
import HeadMeta from '@source/pages/Home/components/HeadMeta';
import useUTM from '@source/hooks/useUTM';

import CCPOLiteComparisonTable from '@source/pages/CarroCertified/components/CCPOLiteComparisonTable';
import CustomerReviewMY from '@source/components/CustomerReview/CustomerReviewMY';
import CCPOPageInspection160PointV2 from '@source/pages/CarroCertified/components/V2/CCPOPageInspection160PointV2';
import CCPOPageFeaturesV2 from '@source/pages/CarroCertified/components/V2/CCPOPageFeaturesV2';
import useMobileView from '@design-system/hooks/useMobileView';

// Style for main content (bg: white)
const StyledMainContent = styled.main`
  margin: 0px auto 40px;
  padding: 24px 11px;
  height: auto;
  background: ${(props) => props.theme.color.surfaceDark};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  filter: drop-shadow(0px 2px 9px rgba(0, 0, 0, 0.12));

  .main-title {
    color: ${(props) => props.theme.color.primaryBase};
    ${(props) => props.theme.typo.familyGoogle.semiBold}
    text-align: center;
    padding-bottom: 36px;
    font-size: 20px;
    line-height: 28px;
  }

  .customer-title {
    span {
      color: ${(props) => props.theme.color.primaryBase};
      font-weight: inherit;
    }
  }

  @media screen and (min-width: 576px) {
    padding: 36px;
  }

  @media screen and (min-width: 1280px) {
    margin-bottom: 80px;
    padding: 59px 43px;

    .main-title {
      font-size: 33.5px;
      line-height: 59px;
    }
  }
`;

const StyledMainContentV2 = styled.main`
  margin: 0px auto 24px;
  padding: 0 10px;
  height: auto;
  background: ${(props) => props.theme.color.surfaceDark};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  filter: drop-shadow(0px 2px 9px rgba(0, 0, 0, 0.12));

  .main-title-v2 {
    color: ${(props) => props.theme.color.primaryBase};
    ${(props) => props.theme.typo.familyGoogle.bold}
    text-align: center;
    padding-bottom: unset;
    font-size: 18px;
    line-height: 25px;
    margin-bottom: 44px;
    padding-top: 55px;
  }

  .customer-title {
    span {
      color: ${(props) => props.theme.color.primaryBase};
      font-weight: inherit;
    }
  }

  @media screen and (min-width: 992px) {
    padding: 0;
  }

  @media screen and (min-width: 1280px) {
    margin-bottom: 34px;

    .main-title-my,
    .main-title-v2 {
      font-size: 30px;
      line-height: 35px;
    }
  }
`;

const CCPOPageContainer = ({ region }: IRegionProps) => {
  const isJP = ['jp'].includes(region);
  const { t } = useTranslation('ccpo');
  // save UTM referrer to cookie
  useUTM();
  const isMobile = useMobileView(992);

  // TODO_CCPO
  const reviewContent = getReviewCCPO();

  let renderMainContent = null;

  switch (region) {
    case 'my':
    case 'hk':
      renderMainContent = (
        <>
          <StyledMainContentV2 role="main" aria-label="Carro Certified Program Content">
            <h1 className="main-title-v2">
              <Trans ns="ccpo" defaults={t(isMobile ? 'ccpo.mainContentTitleV2Mobile' : 'ccpo.mainContentTitleV2')} />
            </h1>
            <section aria-label="Inspection Point">
              <CCPOPageInspection160PointV2 region={region} />
            </section>
            <section aria-label="Features">
              <CCPOPageFeaturesV2 region={region} />
            </section>
          </StyledMainContentV2>
          <StyledMainContentV2 role="complementary" aria-label="Carro Certified Program Comparison">
            <section aria-label="Comparison Table">
              <CCPOLiteComparisonTable region={region} />
            </section>
          </StyledMainContentV2>
          <section aria-label="Customer Reviews">
            <CustomerReviewMY content={reviewContent[region]} region={region} />
          </section>
        </>
      );
      break;
    case 'jp':
      renderMainContent = (
        <StyledMainContentV2 role="main" aria-label="Carro Certified Program Content">
          <h1 className="main-title-v2">
            <Trans ns="ccpo" defaults={t(isMobile ? 'ccpo.mainContentTitleV2Mobile' : 'ccpo.mainContentTitleV2')} />
          </h1>
          <section aria-label="Inspection Point">
            <CCPOPageInspection160PointV2 region={region} />
          </section>
          <section aria-label="Features">
            <CCPOPageFeaturesV2 region={region} />
          </section>
        </StyledMainContentV2>
      );
      break;
    default:
      renderMainContent = (
        <StyledMainContent role="main" aria-label="Carro Certified Program Content">
          <h1 className="main-title">
            <Trans ns="ccpo" defaults={t('ccpo.mainContentTitle')} />
          </h1>
          <section aria-label="Inspection Point">
            <CCPOPageInspection160Point region={region} />
          </section>
          <section aria-label="Features">
            <CCPOPageFeatures region={region} />
            {!isJP && (
              <section aria-label="Customer Reviews">
                <CustomerReview content={reviewContent[region]} region={region} />
              </section>
            )}
          </section>
        </StyledMainContent>
      );
  }

  return (
    <Layout bannerContent={<CCPOPageBanner region={region} />} hideSocialChat={false}>
      <HeadMeta metaKey="ccpoIndex" region={region} />
      {/* main content with bg white */}
      <StyledContainer>{renderMainContent}</StyledContainer>
      <section aria-label="Browse Certified Cars">
        <CCPOBrowseCards region={region} />
      </section>
      {/* Carro was named section with pink bg */}
      <section aria-label="Carro Certified Program Highlights">
        <CCPOPageHighlights />
      </section>
    </Layout>
  );
};

export default CCPOPageContainer;
